/* istanbul ignore file */
import { useContext, useState, useEffect, RefObject } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppContext } from "../app/store";
import {
  Plan,
  SearchResultPlan,
  DrugCostInfo,
  LowIncomeSubsidyStatus,
  PrescriptionFrequency,
  Pharmacy,
  AppState,
  DrugCostsResponse,
  DrugCostPrescriptionInfo,
} from "../@types";
import { getDrugCosts } from "../api";
import { useTranslate } from "./intlHooks";
import { logError } from ".";
import { getFullPlanId } from "./objectUtilities";
import { isSearchResultPlan } from "../@types/guards";

export const getDefaultDrugCostInfo = ({
  plan,
  t,
}: {
  plan: Plan | SearchResultPlan;
  t: (id: string) => string;
}): DrugCostInfo => ({
  costs: [],
  lowest_mail_total: t("plan_card.calculating"),
  lowest_retail_total: t("plan_card.calculating"),
  plan: getFullPlanId(plan),
  excluded_drugs: [],
  restrictions: [],
});

export const getPrescriptionInfoForDrugCosts = (
  state: AppState
): DrugCostPrescriptionInfo[] =>
  state.prescriptions.map(p => ({
    ndc: p.ndc,
    frequency: p.frequency as PrescriptionFrequency,
    quantity: String(p.quantity), // FIXME: Need to update this to use the PDE quantity data if isHidden is true
  }));

export const getPharmacyNpis = (pharmacies: Pharmacy[]) =>
  pharmacies.map(p => p.npi);

export const getLisForDrugCosts = ({
  plan,
  state,
  mctCurrentPlanYear,
}: {
  plan: Plan | SearchResultPlan;
  state: AppState;
  mctCurrentPlanYear: string;
}): LowIncomeSubsidyStatus => {
  let lis = state.lis ? state.lis : LowIncomeSubsidyStatus.LIS_NO_HELP;
  // anon users do not use futureLis
  if (plan.contract_year != mctCurrentPlanYear && state.futureLis) {
    lis = state.futureLis;
  }
  return lis;
};

export const fetchCostsReturnHandler = (
  res: DrugCostsResponse | undefined,
  defaultDrugCostInfo: DrugCostInfo
) => (res ? res.plans[0] : defaultDrugCostInfo);

/**
 * Optional `fullYearPricing` and `retailOnly` both default to `false`
 */
export const fetchCosts = ({
  state,
  pharmacies,
  plan,
  mctCurrentPlanYear,
  fullYearPricing = false,
  retailOnly = false,
  t,
}: {
  state: AppState;
  pharmacies: Pharmacy[];
  plan: Plan | SearchResultPlan;
  mctCurrentPlanYear: string;
  fullYearPricing?: boolean;
  retailOnly?: boolean;
  t: (id: string) => string;
}): Promise<DrugCostInfo> => {
  const defaultDrugCostInfo = getDefaultDrugCostInfo({ plan, t });
  const prescriptions = getPrescriptionInfoForDrugCosts(state);
  const npis = getPharmacyNpis(pharmacies);
  const planId = getFullPlanId(plan);

  const lis = getLisForDrugCosts({ plan, state, mctCurrentPlanYear });

  return getDrugCosts({
    npis,
    prescriptions,
    planIds: [planId],
    lis,
    fullYearPricing,
    retailOnly,
  }).then(
    res => {
      return fetchCostsReturnHandler(res, defaultDrugCostInfo);
    },
    e => {
      logError("Could not get drug costs (costsHelpers/fetchCosts)", e);
      return defaultDrugCostInfo;
    }
  );
};

export const useCosts = (
  plan: Plan | SearchResultPlan,
  ref?: RefObject<HTMLElement>,
  pharmacies?: Pharmacy[],
  fullYearPricing = false
): DrugCostInfo => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const { mctCurrentPlanYear } = useFlags();
  const defaultDrugCostInfo = getDefaultDrugCostInfo({ plan, t });
  const [costInfo, setCostInfo] = useState<DrugCostInfo>(defaultDrugCostInfo);
  const pharmaciesToQuery = pharmacies || state.pharmacies;

  const updateCosts = (costs: DrugCostInfo): void => {
    setCostInfo(costs);
    observer.disconnect();
  };

  const fetchCostsAndUpdate = () => {
    fetchCosts({
      state,
      pharmacies: pharmaciesToQuery,
      plan,
      mctCurrentPlanYear,
      fullYearPricing,
      t,
    }).then(drugCostInfo => {
      updateCosts(drugCostInfo);
    });
  };

  const observer = new IntersectionObserver(
    ([entry]: IntersectionObserverEntry[]): void => {
      if (entry.isIntersecting) {
        fetchCostsAndUpdate();
      }
    }
  );

  useEffect(() => {
    if (!mctCurrentPlanYear) {
      return;
    }
    if (isSearchResultPlan(plan) && plan.remaining_premium_and_drugs) {
      return;
    }

    if (ref) {
      if (ref.current) {
        observer.observe(ref.current);
      }
      return (): void => observer.disconnect();
    } else {
      fetchCostsAndUpdate();
      return;
    }
  }, [plan, mctCurrentPlanYear]);

  return costInfo;
};
